import { ReactNode } from 'react';
import { Icon, Typography } from '@vp/swan';

export type FeatureKey =
    | 'suitableFor'
    | 'turnaroundTime'
    | 'matchingDesignDetails'
    | 'revisions'
    | 'designerMatching'
    | 'fileFormat'
    | 'revisionComments'
    | 'messaging';

export type Feature = {
    title: string;
    description?: {
        text: string;
        amount?: number;
    };
    mobileDescription?: string;
    icon?: JSX.Element;
    popover?: ReactNode;
};

export type Features = Record<FeatureKey, Feature>;

export const baseFeatures: Features = {
    suitableFor: {
        title: 'Suitable for',
        description: { text: 'Simple designs at an affordable price' },
        icon: <Icon iconType="consumer" size="standard" skin="standard" />,
    },
    turnaroundTime: {
        title: 'Design turnaround time',
        description: { text: '24-hour turnaround' },
        mobileDescription: '24-hour design turnaround time (excludes weekends and public holidays)',
        icon: <Icon iconType="orderHistory" size="standard" skin="subtle" />,
        popover:
            'Turnaround time refers to the time it takes for the designer to deliver your initial design and for each revision round.',
    },
    matchingDesignDetails: {
        title: 'Matching design details',
        description: { text: 'Be assigned a designer' },
        icon: <Icon iconType="account" size="standard" skin="subtle" />,
    },
    revisions: {
        title: 'Number of revisions',
        description: { text: 'rounds of revisions', amount: 3 },
        mobileDescription: '3 rounds of revisions to use within 30 days',
        icon: <Icon iconType="edit" size="standard" skin="subtle" />,
        popover: (
            <Typography>
                A revision is feedback provided to the designer based on the initial design concept
                they provide – they are not a complete change of direction or a new design.
                <br />
                <br />
                Simple Designs come with 3 rounds of revisions to use within 30 days after the
                designer was assigned to you.
                <br />
                <br />
                Exclusive Designs include 5 rounds of revisions to use within 30 days after choosing
                your designer.
            </Typography>
        ),
    },
    designerMatching: {
        title: 'Designer matching',
        description: { text: 'Be assigned an available designer' },
        popover: (
            <Typography>
                Your Simple Design project will be assigned to the first available designer.
                <br />
                <br />
                For an Exclusive Design project, you can view designers' pitches, reviews and
                portfolios to pick the right one for you.
            </Typography>
        ),
    },
    fileFormat: {
        title: 'Deliverable',
        description: {
            text: 'Design delivered to your VistaPrint Account ready to print',
        },
    },
    revisionComments: {
        title: 'Leave async feedback',
        description: { text: 'Leave async feedback' },
        popover: 'Review and leave comments on the design in your own time.',
    },
    messaging: {
        title: 'Messaging',
        description: { text: 'n/a' },
        mobileDescription: 'Messaging is not available',
        popover:
            'Messages can be exchanged between you and the designer to clarify your project requirements.',
    },
};

export const getFeatureSubset = (features: Features, keys: FeatureKey[]): Feature[] => {
    return keys.map((key) => features[key]);
};

export const getFeature = (features: Features, key: FeatureKey): Feature => {
    return features[key];
};
